/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';

import { CalendlyAuthorization } from '../models/calendly-authorization';
import { CalendlyEventType } from '../models/calendly-event-type';
import { CalendlyEventTypes } from '../models/calendly-event-types';
import { CalendlyLocations } from '../models/calendly-locations';
import { CalendlyOAuthStatus } from '../models/calendly-o-auth-status';
import { CalendlyReservations } from '../models/calendly-reservations';
import { completeCalendlyOAuth } from '../fn/calendly-o-auth-api/complete-calendly-o-auth';
import { CompleteCalendlyOAuth$Params } from '../fn/calendly-o-auth-api/complete-calendly-o-auth';
import { createCalendlyEventType } from '../fn/calendly-o-auth-api/create-calendly-event-type';
import { CreateCalendlyEventType$Params } from '../fn/calendly-o-auth-api/create-calendly-event-type';
import { disconnectCalendlyOAuth } from '../fn/calendly-o-auth-api/disconnect-calendly-o-auth';
import { DisconnectCalendlyOAuth$Params } from '../fn/calendly-o-auth-api/disconnect-calendly-o-auth';
import { getCalendlyOAuthStatus } from '../fn/calendly-o-auth-api/get-calendly-o-auth-status';
import { GetCalendlyOAuthStatus$Params } from '../fn/calendly-o-auth-api/get-calendly-o-auth-status';
import { listCalendlyEventTypes } from '../fn/calendly-o-auth-api/list-calendly-event-types';
import { ListCalendlyEventTypes$Params } from '../fn/calendly-o-auth-api/list-calendly-event-types';
import { listCalendlyLocations } from '../fn/calendly-o-auth-api/list-calendly-locations';
import { ListCalendlyLocations$Params } from '../fn/calendly-o-auth-api/list-calendly-locations';
import { listCalendlyReservations } from '../fn/calendly-o-auth-api/list-calendly-reservations';
import { ListCalendlyReservations$Params } from '../fn/calendly-o-auth-api/list-calendly-reservations';
import { startCalendlyOAuth } from '../fn/calendly-o-auth-api/start-calendly-o-auth';
import { StartCalendlyOAuth$Params } from '../fn/calendly-o-auth-api/start-calendly-o-auth';

@Injectable({ providedIn: 'root' })
export class CalendlyOAuthApiService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getCalendlyOAuthStatus()` */
  static readonly GetCalendlyOAuthStatusPath = '/v1/api/ui/calendly-demo/oauth/status';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getCalendlyOAuthStatus()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCalendlyOAuthStatus$Response(params?: GetCalendlyOAuthStatus$Params, context?: HttpContext): Observable<StrictHttpResponse<CalendlyOAuthStatus>> {
    return getCalendlyOAuthStatus(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getCalendlyOAuthStatus$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCalendlyOAuthStatus(params?: GetCalendlyOAuthStatus$Params, context?: HttpContext): Observable<CalendlyOAuthStatus> {
    return this.getCalendlyOAuthStatus$Response(params, context).pipe(
      map((r: StrictHttpResponse<CalendlyOAuthStatus>): CalendlyOAuthStatus => r.body)
    );
  }

  /** Path part for operation `listCalendlyEventTypes()` */
  static readonly ListCalendlyEventTypesPath = '/v1/api/ui/calendly-demo/event-types';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listCalendlyEventTypes()` instead.
   *
   * This method doesn't expect any request body.
   */
  listCalendlyEventTypes$Response(params?: ListCalendlyEventTypes$Params, context?: HttpContext): Observable<StrictHttpResponse<CalendlyEventTypes>> {
    return listCalendlyEventTypes(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listCalendlyEventTypes$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listCalendlyEventTypes(params?: ListCalendlyEventTypes$Params, context?: HttpContext): Observable<CalendlyEventTypes> {
    return this.listCalendlyEventTypes$Response(params, context).pipe(
      map((r: StrictHttpResponse<CalendlyEventTypes>): CalendlyEventTypes => r.body)
    );
  }

  /** Path part for operation `createCalendlyEventType()` */
  static readonly CreateCalendlyEventTypePath = '/v1/api/ui/calendly-demo/event-types';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createCalendlyEventType()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createCalendlyEventType$Response(params: CreateCalendlyEventType$Params, context?: HttpContext): Observable<StrictHttpResponse<CalendlyEventType>> {
    return createCalendlyEventType(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createCalendlyEventType$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createCalendlyEventType(params: CreateCalendlyEventType$Params, context?: HttpContext): Observable<CalendlyEventType> {
    return this.createCalendlyEventType$Response(params, context).pipe(
      map((r: StrictHttpResponse<CalendlyEventType>): CalendlyEventType => r.body)
    );
  }

  /** Path part for operation `listCalendlyLocations()` */
  static readonly ListCalendlyLocationsPath = '/v1/api/ui/calendly-demo/locations';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listCalendlyLocations()` instead.
   *
   * This method doesn't expect any request body.
   */
  listCalendlyLocations$Response(params?: ListCalendlyLocations$Params, context?: HttpContext): Observable<StrictHttpResponse<CalendlyLocations>> {
    return listCalendlyLocations(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listCalendlyLocations$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listCalendlyLocations(params?: ListCalendlyLocations$Params, context?: HttpContext): Observable<CalendlyLocations> {
    return this.listCalendlyLocations$Response(params, context).pipe(
      map((r: StrictHttpResponse<CalendlyLocations>): CalendlyLocations => r.body)
    );
  }

  /** Path part for operation `listCalendlyReservations()` */
  static readonly ListCalendlyReservationsPath = '/v1/api/ui/calendly-demo/event-types/{eventTypeUuid}/reservations';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listCalendlyReservations()` instead.
   *
   * This method doesn't expect any request body.
   */
  listCalendlyReservations$Response(params: ListCalendlyReservations$Params, context?: HttpContext): Observable<StrictHttpResponse<CalendlyReservations>> {
    return listCalendlyReservations(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listCalendlyReservations$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listCalendlyReservations(params: ListCalendlyReservations$Params, context?: HttpContext): Observable<CalendlyReservations> {
    return this.listCalendlyReservations$Response(params, context).pipe(
      map((r: StrictHttpResponse<CalendlyReservations>): CalendlyReservations => r.body)
    );
  }

  /** Path part for operation `startCalendlyOAuth()` */
  static readonly StartCalendlyOAuthPath = '/v1/api/ui/calendly-demo/oauth/connect';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `startCalendlyOAuth()` instead.
   *
   * This method doesn't expect any request body.
   */
  startCalendlyOAuth$Response(params?: StartCalendlyOAuth$Params, context?: HttpContext): Observable<StrictHttpResponse<CalendlyAuthorization>> {
    return startCalendlyOAuth(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `startCalendlyOAuth$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  startCalendlyOAuth(params?: StartCalendlyOAuth$Params, context?: HttpContext): Observable<CalendlyAuthorization> {
    return this.startCalendlyOAuth$Response(params, context).pipe(
      map((r: StrictHttpResponse<CalendlyAuthorization>): CalendlyAuthorization => r.body)
    );
  }

  /** Path part for operation `disconnectCalendlyOAuth()` */
  static readonly DisconnectCalendlyOAuthPath = '/v1/api/ui/calendly-demo/oauth/connection';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `disconnectCalendlyOAuth()` instead.
   *
   * This method doesn't expect any request body.
   */
  disconnectCalendlyOAuth$Response(params?: DisconnectCalendlyOAuth$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return disconnectCalendlyOAuth(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `disconnectCalendlyOAuth$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  disconnectCalendlyOAuth(params?: DisconnectCalendlyOAuth$Params, context?: HttpContext): Observable<void> {
    return this.disconnectCalendlyOAuth$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `completeCalendlyOAuth()` */
  static readonly CompleteCalendlyOAuthPath = '/v1/api/ui/public/calendly/oauth/callback';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `completeCalendlyOAuth()` instead.
   *
   * This method doesn't expect any request body.
   */
  completeCalendlyOAuth$Response(params?: CompleteCalendlyOAuth$Params, context?: HttpContext): Observable<StrictHttpResponse<string>> {
    return completeCalendlyOAuth(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `completeCalendlyOAuth$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  completeCalendlyOAuth(params?: CompleteCalendlyOAuth$Params, context?: HttpContext): Observable<string> {
    return this.completeCalendlyOAuth$Response(params, context).pipe(
      map((r: StrictHttpResponse<string>): string => r.body)
    );
  }

}
